import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Typography, Tag, Space, Descriptions, Spin, Button, Alert } from "antd";
import { EyeOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { useSource } from "../../hooks/useSource";
import JsonViewer from "../common/JsonViewer";
import { originLabels } from "../../constants/originLabels";

const { Text } = Typography;

/**
 * Formata um CNJ para o formato padrão (NNNNNNN-DD.AAAA.J.TR.OOOO)
 */
const formatCNJ = (cnj: string): string => {
  if (!cnj) return "";
  // Remove caracteres não numéricos
  const cleaned = cnj.replace(/\D/g, "");
  // Verifica se tem 20 dígitos
  if (cleaned.length !== 20) return cnj; // Retorna original se não tiver 20 dígitos
  
  // Formata: NNNNNNN-DD.AAAA.J.TR.OOOO
  const formatted = `${cleaned.slice(0, 7)}-${cleaned.slice(7, 9)}.${cleaned.slice(9, 13)}.${cleaned.slice(13, 14)}.${cleaned.slice(14, 16)}.${cleaned.slice(16, 20)}`;
  return formatted;
};

/**
 * Calcula a diferença de tempo entre duas datas e retorna uma string formatada
 */
const calculateTimeDifference = (createdAt: string | Date, respondedAt: string | Date | null): string | null => {
  if (!createdAt || !respondedAt) return null;
  
  try {
    const created = new Date(createdAt);
    const responded = new Date(respondedAt);
    
    if (isNaN(created.getTime()) || isNaN(responded.getTime())) {
      return null;
    }
    
    const diffMs = responded.getTime() - created.getTime();
    
    if (diffMs < 0) return null; // Se respondedAt é anterior a createdAt, retorna null
    
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h ${diffMinutes % 60}m ${diffSeconds % 60}s`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m ${diffSeconds % 60}s`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ${diffSeconds % 60}s`;
    } else {
      return `${diffSeconds}s`;
    }
  } catch (error) {
    console.error("Erro ao calcular diferença de tempo:", error);
    return null;
  }
};

/**
 * Extrai CNJs do body do Push recursivamente
 */
const extractCNJsFromBody = (body: any, cnjs: Set<string> = new Set(), visited = new WeakSet()): string[] => {
  if (!body) return [];
  
  // Previne loops infinitos em objetos circulares
  if (typeof body === "object" && visited.has(body)) {
    return Array.from(cnjs);
  }
  if (typeof body === "object") {
    visited.add(body);
  }
  
  if (Array.isArray(body)) {
    body.forEach((item) => {
      extractCNJsFromBody(item, cnjs, visited);
    });
  } else if (typeof body === "object" && body !== null) {
    // Procura por campos que podem conter CNJ
    Object.keys(body).forEach((key) => {
      const value = body[key];
      const keyLower = key.toLowerCase();
      
      // Verifica se a chave indica um CNJ
      if (keyLower === "cnj" || keyLower === "cnjnumber" || keyLower === "numerocnj" || keyLower === "numero_cnj") {
        if (typeof value === "string" && value.trim()) {
          cnjs.add(value.trim());
        } else if (typeof value === "number") {
          cnjs.add(String(value));
        }
      }
      
      // Continua a busca recursiva
      if (value !== null && typeof value === "object") {
        extractCNJsFromBody(value, cnjs, visited);
      }
    });
  }
  
  return Array.from(cnjs);
};

interface SourceViewerProps {
  sourceId: string;
}

/**
 * Componente para visualizar uma fonte individual.
 *
 * Exibe os dados da fonte encontrada e a origem (API) onde foi encontrada.
 */
const SourceViewer: React.FC<SourceViewerProps> = ({ sourceId }) => {
  const { getSource } = useSource();
  const [sourceData, setSourceData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cwErrorsOpen, setCwErrorsOpen] = useState<boolean>(false);
  const [cwWarningsOpen, setCwWarningsOpen] = useState<boolean>(false);

  /**
   * Carrega os dados da fonte.
   */
  const loadSource = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getSource(sourceId);
      setSourceData(result);
    } catch (err: any) {
      console.error("Erro ao buscar fonte:", err);
      setError(err.message || "Erro ao buscar fonte");
      setSourceData(null);
    } finally {
      setLoading(false);
    }
  }, [sourceId, getSource]);

  /**
   * Efeito para carregar fonte inicialmente.
   */
  useEffect(() => {
    loadSource();
  }, [loadSource]);

  // Calcula variáveis derivadas antes dos retornos condicionais (para hooks)
  const origin = sourceData?.key || "not-found";
  const originLabel = originLabels[origin] || "Não encontrada";
  const found = origin !== "not-found" && sourceData?.data;
  const deleted = sourceData?.deleted === true;
  const isDatateamOrCodilodt = origin.startsWith("codilo-dt-") || origin.startsWith("datateam-");
  const frequencyType = sourceData?.frequencyType;
  // "logged" pode vir em múltiplos locais dependendo da fonte:
  // - sourceData.logged (campo agregado)
  // - sourceData.data.logged (dentro do payload)
  // - sourceData.data?.data?.logged (algumas APIs aninham em data.data)
  const logged = sourceData?.logged ?? sourceData?.data?.logged ?? sourceData?.data?.data?.logged ?? false;
  // Detecta se é push
  const isPush = origin === "push" || originLabel === "Push" || originLabel.toLowerCase().includes("push");
  // Detecta se é autorequest
  const isAutorequest = origin === "autorequest" || sourceData?.isAutorequest === true;
  // Só exibe alertas de CapturaWeb quando a origem é baseada em CNJ.
  // Assim evitamos ruído em buscas por OAB, documento, nome da parte ou nome do advogado.
  const isCnjBasedCapturaWeb = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData) return false;

    const normalizeKey = (value: unknown): string => {
      if (typeof value !== "string") return "";
      return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    };

    const candidates = [
      sourceData.search,
      sourceData.paramKey,
      sourceData.param?.key,
      sourceData.data?.search,
      sourceData.data?.paramKey,
      sourceData.data?.param?.key,
      sourceData.data?.requested?.search,
      sourceData.data?.requested?.paramKey,
      sourceData.data?.requested?.param?.key,
    ];

    return candidates.some((value) => {
      const normalized = normalizeKey(value);
      return normalized === "cnj" || normalized === "numerocnj" || normalized === "cnjnumber";
    });
  }, [origin, sourceData]);

  // Extrai e formata CNJs do body do Push (deve estar antes dos retornos condicionais)
  const pushCNJs = useMemo(() => {
    if (!isPush || !sourceData?.data) return [];
    const cnjs = new Set<string>();
    
    // Busca recursivamente no body
    const foundCNJs = extractCNJsFromBody(sourceData.data);
    foundCNJs.forEach((cnj) => cnjs.add(cnj));
    
    // Garante que também pega data.cnj especificamente
    if (sourceData.data?.data?.cnj) {
      const cnjValue = sourceData.data.data.cnj;
      if (typeof cnjValue === "string" && cnjValue.trim()) {
        cnjs.add(cnjValue.trim());
      } else if (typeof cnjValue === "number") {
        cnjs.add(String(cnjValue));
      }
    }
    
    // Também verifica sourceData.cnj se existir
    if (sourceData.cnj) {
      cnjs.add(sourceData.cnj);
    }
    
    return Array.from(cnjs).map(formatCNJ).filter((cnj) => cnj.length > 0);
  }, [isPush, sourceData?.data, sourceData?.cnj]);

  // Informações agregadas específicas para CapturaWeb
  const capturaWebStatus = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData) return null;
    // Prioriza status dentro de data.requested (API CapturaWeb), mas aceita também `type`
    const requestedStatus =
      sourceData.data?.requested?.status ||
      sourceData.data?.requested?.statusCode ||
      sourceData.data?.requested?.type ||
      null;

    const dataLevelStatus = sourceData.data?.status || sourceData.data?.statusCode || sourceData.data?.type || null;
    const rootStatus = sourceData.status || sourceData.statusLabel || sourceData.statusCode || null;

    return requestedStatus || dataLevelStatus || rootStatus;
  }, [origin, sourceData]);

  const capturaWebStatusLevel = useMemo<"success" | "warning" | "error" | "info">(() => {
    if (!capturaWebStatus) return "info";
    const normalized = String(capturaWebStatus).toLowerCase();
    if (normalized.includes("error") || normalized.includes("erro") || normalized.includes("fail")) return "error";
    if (normalized.includes("warn") || normalized.includes("alerta")) return "warning";
    return "success";
  }, [capturaWebStatus]);

  // Mensagem relacionada ao status — tenta extrair de vários locais,
  // priorizando data.data.message (conforme solicitado: json > data).
  const capturaWebStatusMessage = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData) return null;
    // Mensagem pode estar em vários locais e pode ser string ou objeto
    const candidate =
      sourceData.data?.data ||
      sourceData.data?.data?.message ||
      sourceData.data?.message ||
      sourceData.data?.errorMessage ||
      sourceData.statusMessage ||
      sourceData.message ||
      null;

    // Se candidate for objeto com campo message, retorna esse; se for string, retorna direto.
    if (candidate && typeof candidate === "object" && candidate.message) {
      return String(candidate.message);
    }
    if (candidate && typeof candidate === "string") {
      return candidate;
    }
    return null;
  }, [origin, sourceData]);

  // Campo "query" pode vir em diferentes caminhos; tenta vários fallbacks
  const capturaWebQuery = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData) return null;
    if (sourceData.query != null && sourceData.query !== "") return sourceData.query;
    if (sourceData.data?.requested?.query != null && sourceData.data.requested.query !== "") return sourceData.data.requested.query;
    if (sourceData.data?.data?.query != null && sourceData.data.data.query !== "") return sourceData.data.data.query;
    return null;
  }, [origin, sourceData]);

  const capturaWebTimeDiff = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData) return null;

    let createdAt: string | Date | null = null;
    let respondedAt: string | Date | null = null;
    let visited = new WeakSet<object>();

    const findDates = (obj: any, depth = 0) => {
      if (depth > 5) return;
      if (!obj || typeof obj !== "object") return;

      if (visited.has(obj)) return;
      visited.add(obj);

      if (Array.isArray(obj)) {
        obj.forEach((item) => findDates(item, depth + 1));
        return;
      }

      if (obj.requested) {
        if (!createdAt && (obj.requested.createdAt || obj.requested.created_at)) {
          createdAt = obj.requested.createdAt || obj.requested.created_at;
        }
        if (!respondedAt && (obj.requested.respondedAt || obj.requested.responded_at)) {
          respondedAt = obj.requested.respondedAt || obj.requested.responded_at;
        }
      }

      if (!createdAt && (obj.createdAt || obj.created_at)) {
        createdAt = obj.createdAt || obj.created_at;
      }
      if (!respondedAt && (obj.respondedAt || obj.responded_at)) {
        respondedAt = obj.respondedAt || obj.responded_at;
      }

      if (createdAt && respondedAt) return;

      Object.keys(obj).forEach((key) => {
        if (key !== "requested" && typeof obj[key] === "object" && obj[key] !== null) {
          findDates(obj[key], depth + 1);
        }
      });
    };

    if (sourceData.data) {
      findDates(sourceData.data);
    }

    if ((!createdAt || !respondedAt) && sourceData) {
      visited = new WeakSet<object>();
      findDates(sourceData);
    }

    if (!createdAt || !respondedAt) return null;

    return calculateTimeDifference(createdAt, respondedAt);
  }, [origin, sourceData]);
  
  

  // Extrai CNJs dos pushIds para origens Codilo DT / DataTeam
  const codiloDataCNJs = useMemo(() => {
    if (!isDatateamOrCodilodt || !sourceData?.data?.pushIds) return [];
    return (sourceData.data.pushIds as any[])
      .map((p: any) => p?.cnj)
      .filter((cnj): cnj is string => Boolean(cnj))
      .map(formatCNJ);
  }, [isDatateamOrCodilodt, sourceData?.data?.pushIds]);

  // Verifica warnings nos dados da fonte CapturaWeb — campos vazios por processo
  const capturaWebDataWarnings = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData?.data || !isCnjBasedCapturaWeb) return [];

    const warnings: string[] = [];
    const items: any[] = Array.isArray(sourceData.data?.data) ? sourceData.data.data : [];

    let coverVazioCount = 0;
    let propertiesVazioCount = 0;
    let peopleVazioCount = 0;
    let stepsVazioCount = 0;

    items.forEach((item: any) => {
      // cover é array
      if (!Array.isArray(item?.cover) || item.cover.length === 0) coverVazioCount++;
      // properties é objeto — vazio se null, undefined ou sem chaves
      if (!item?.properties || Object.keys(item.properties).length === 0) propertiesVazioCount++;
      // people é array
      if (!Array.isArray(item?.people) || item.people.length === 0) peopleVazioCount++;
      // steps é array
      if (!Array.isArray(item?.steps) || item.steps.length === 0) stepsVazioCount++;
    });

    if (coverVazioCount > 0) warnings.push(`cover vazio (${coverVazioCount} processo(s))`);
    if (propertiesVazioCount > 0) warnings.push(`properties vazio (${propertiesVazioCount} processo(s))`);
    if (peopleVazioCount > 0) warnings.push(`people vazio (${peopleVazioCount} processo(s))`);
    if (stepsVazioCount > 0) warnings.push(`steps vazio (${stepsVazioCount} processo(s))`);

    return warnings;
  }, [origin, sourceData, isCnjBasedCapturaWeb]);

  // Valida erros nos dados da fonte CapturaWeb (properties e people)
  // Estrutura real: sourceData.data.data = array de itens (lawsuits)
  // Cada item: { cover, properties (objeto), people (array), steps }
  const capturaWebDataErrors = useMemo(() => {
    if (origin !== "capturaweb" || !sourceData?.data || !isCnjBasedCapturaWeb) return [];

    const errors: string[] = [];

    // data.data é o array de processos retornado pela CapturaWeb
    const items: any[] = Array.isArray(sourceData.data?.data) ? sourceData.data.data : [];

    let semCnjCount = 0;
    let semDegreeCount = 0;
    let cnjInvalidoCount = 0;
    let semPoleCount = 0;

    items.forEach((item: any) => {
      // properties é um objeto por processo (não um array)
      const props = item?.properties;
      if (props && typeof props === "object" && !Array.isArray(props)) {
        if (!props.cnj) semCnjCount++;
        if (!props.degree) semDegreeCount++;
        if (props.cnj) {
          // Valida formato exato: NNNNNNN-DD.AAAA.J.TT.OOOO (Resolução CNJ nº 65/2008)
          const CNJ_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}$/;
          if (!CNJ_REGEX.test(String(props.cnj).trim())) cnjInvalidoCount++;
        }
      }

      // people é um array por processo
      const people: any[] = Array.isArray(item?.people) ? item.people : [];
      people.forEach((person: any) => {
        if (!person?.pole) semPoleCount++;
      });
    });

    if (semCnjCount > 0) errors.push(`properties sem cnj (${semCnjCount} processo(s))`);
    if (semDegreeCount > 0) errors.push(`properties sem degree (${semDegreeCount} processo(s))`);
    if (cnjInvalidoCount > 0) errors.push(`properties com cnj inválido (${cnjInvalidoCount} processo(s))`);
    if (semPoleCount > 0) errors.push(`people sem pole (${semPoleCount} pessoa(s))`);

    return errors;
  }, [origin, sourceData, isCnjBasedCapturaWeb]);

  if (loading) {
    return (
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text strong>ID: </Text>
          <Text copyable code>
            {sourceId}
          </Text>
          <Spin size="large" />
        </Space>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Text copyable code style={{ fontSize: "14px" }}>
              {sourceId}
            </Text>
            <Tag color="error" style={{ fontSize: "13px", padding: "3px 10px" }}>
              Erro ao buscar fonte
            </Tag>
          </div>
        </Space>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Text copyable code style={{ fontSize: "14px" }}>
            {sourceId}
          </Text>
          <Tag color={found ? "success" : "error"} style={{ fontSize: "13px", padding: "3px 10px" }}>
            {originLabel}
          </Tag>
          {found && logged && (
            <Tag color="green" style={{ fontSize: "13px", padding: "3px 10px" }}>Logado</Tag>
          )}
          {/* frequencyType ao lado do origin para origens Codilo DT / DataTeam */}
          {isDatateamOrCodilodt && found && frequencyType && (
            <Tag
              color={frequencyType === "daily" ? "blue" : "default"}
              style={{ fontSize: "12px" }}
            >
              {frequencyType === "daily" ? "Daily" : frequencyType === "never" ? "Never" : frequencyType}
            </Tag>
          )}
          {/* Botão Ver push ao lado do frequencyType */}
          {isDatateamOrCodilodt && found && frequencyType === "daily" && sourceData?.pushIds &&
            (sourceData.pushIds as string[]).map((pushId: string, idx: number) => (
              <Button
                key={idx}
                type="primary"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => window.open(`/push-details/${pushId}`, "_blank")}
              >
                Ver push
              </Button>
            ))
          }
        </div>

        {deleted && sourceData.deletedDate && (
          <div style={{ marginTop: "8px" }}>
            <Tag color="error" style={{ fontSize: "12px" }}>
              {isPush ? "Push deletado em" : "Demanda deletada em"} {sourceData.deletedDate}
            </Tag>
          </div>
        )}

        {found && (
          <div>
            {/* Informações específicas para CapturaWeb */}
            {origin === "capturaweb" && (
              <>
                {/* Ordem: platform, search, query, param, paramKey */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
                  <Tag color="cyan" style={{ fontSize: "12px" }}>{sourceData.platform || "-"}</Tag>
                  <Tag color="volcano" style={{ fontSize: "12px" }}>{sourceData.search || "-"}</Tag>
                  <Tag color="green" style={{ fontSize: "12px" }}>{String(capturaWebQuery ?? "-")}</Tag>
                  <Tag color="geekblue" style={{ fontSize: "12px" }}>{String(sourceData.param ?? "-")}</Tag>
                  <Tag color="purple" style={{ fontSize: "12px" }}>{String(sourceData.paramKey ?? "-")}</Tag>
                </div>

                {/* Linha abaixo: tempo e status */}
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
                  <Tag color="blue" style={{ fontSize: "12px" }}>{capturaWebTimeDiff ? `Tempo: ${capturaWebTimeDiff}` : "Tempo: -"}</Tag>
                  <Tag
                    color={capturaWebStatusLevel === "error" ? "error" : capturaWebStatusLevel === "warning" ? "warning" : capturaWebStatusLevel === "success" ? "success" : "default"}
                    style={{ fontSize: "12px" }}
                  >
                    {capturaWebStatus || "-"}
                  </Tag>
                </div>

                {/* Mensagem detalhada quando status é warning ou error (pesquisar em data) */}
                {(capturaWebStatusLevel === "warning" || capturaWebStatusLevel === "error") && capturaWebStatusMessage && (
                  <div style={{ marginTop: 16 }}>
                    <Tag
                      color={capturaWebStatusLevel === "error" ? "error" : "warning"}
                      style={{ fontSize: "12px" }}
                    >
                      {String(capturaWebStatusMessage)}
                    </Tag>
                  </div>
                )}
                {sourceData.cwCounts && (
                  <div style={{ marginTop: "8px" }}>
                    <Space wrap size="small">
                      {sourceData.cwCounts.cover > 0 && (
                        <Tag color="default" style={{ fontSize: "12px" }}>Cover: {sourceData.cwCounts.cover}</Tag>
                      )}
                      {sourceData.cwCounts.properties > 0 && (
                        <Tag color="default" style={{ fontSize: "12px" }}>Properties: {sourceData.cwCounts.properties}</Tag>
                      )}
                      {sourceData.cwCounts.people > 0 && (
                        <Tag color="default" style={{ fontSize: "12px" }}>People: {sourceData.cwCounts.people}</Tag>
                      )}
                      {sourceData.cwCounts.steps > 0 && (
                        <Tag color="default" style={{ fontSize: "12px" }}>Steps: {sourceData.cwCounts.steps}</Tag>
                      )}
                    </Space>
                  </div>
                )}
              </>
            )}

            {/* Informações específicas para Autorequest */}
            {isAutorequest && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: "8px" }}>
                {sourceData.cnj && (
                  <Tag color="blue" style={{ fontSize: "13px" }}>{sourceData.cnj}</Tag>
                )}
                {sourceData.infoCounts && (
                  <>
                    {sourceData.infoCounts.error > 0 && (
                      <Tag color="error" style={{ fontSize: "12px" }}>Error: {sourceData.infoCounts.error}</Tag>
                    )}
                    {sourceData.infoCounts.warning > 0 && (
                      <Tag color="warning" style={{ fontSize: "12px" }}>Warning: {sourceData.infoCounts.warning}</Tag>
                    )}
                    {sourceData.infoCounts.success > 0 && (
                      <Tag color="success" style={{ fontSize: "12px" }}>Success: {sourceData.infoCounts.success}</Tag>
                    )}
                    {sourceData.infoCounts.pending > 0 && (
                      <Tag color="processing" style={{ fontSize: "12px" }}>Pending: {sourceData.infoCounts.pending}</Tag>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Informações específicas para DataTeam/CodiloDT */}
            {isDatateamOrCodilodt && (
              <>
                {sourceData.cnpj && (
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ fontSize: "13px" }}>CNPJ: </Text>
                    <Text copyable style={{ fontSize: "13px" }}>{sourceData.cnpj}</Text>
                  </div>
                )}
                {frequencyType === "daily" && sourceData.pushIds && sourceData.pushIds.length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <Text style={{ fontSize: "13px", marginRight: "4px" }}>Push:</Text>
                    {(sourceData.pushIds as string[]).map((pushId: string, idx: number) => (
                      <Text key={idx} copyable code style={{ fontSize: "13px", marginLeft: idx > 0 ? "8px" : "0" }}>
                        {pushId}
                      </Text>
                    ))}
                  </div>
                )}
                {/* CNJs abaixo do push com label "CNJ:" */}
                {sourceData.cnjs && Array.isArray(sourceData.cnjs) && sourceData.cnjs.length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ fontSize: "13px", marginRight: "4px" }}>CNJ:</Text>
                    <Space wrap size="small">
                      {sourceData.cnjs.map((cnj: string, idx: number) => (
                        <Tag key={idx} color="blue" style={{ fontSize: "12px" }}>
                          {cnj}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
                {/* CNJs extraídos de pushIds[].cnj (ex: Codilo DT - Saj) com label "CNJ:" */}
                {codiloDataCNJs.length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ fontSize: "13px", marginRight: "4px" }}>CNJ:</Text>
                    <Space wrap size="small">
                      {codiloDataCNJs.map((cnj: string, idx: number) => (
                        <Tag key={idx} color="blue" style={{ fontSize: "12px" }}>
                          {cnj}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
                {sourceData.statusCounts && Object.keys(sourceData.statusCounts).length > 0 && (
                  <div style={{ marginBottom: "8px" }}>
                    <Text strong style={{ fontSize: "13px" }}>Processos por Status: </Text>
                    <Space wrap size="small" style={{ marginTop: "4px" }}>
                      {Object.entries(sourceData.statusCounts as Record<string, number>).map(([status, count]: [string, number]) => (
                        <Tag
                          key={status}
                          color={status === "200" ? "success" : parseInt(status) >= 400 ? "error" : "default"}
                          style={{ fontSize: "12px" }}
                        >
                          {`${count} ${status}`}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
              </>
            )}

            {/* Informações específicas para Push */}
            {origin === "push" && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: "12px" }}>
                {pushCNJs.length > 0 && pushCNJs.map((cnj, idx) => (
                  <Tag key={idx} color="blue" style={{ fontSize: "12px" }}>
                    {cnj}
                  </Tag>
                ))}

                {sourceData.infoCounts && (
                  <>
                    {sourceData.infoCounts.error > 0 && (
                      <Tag color="error" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Error: {sourceData.infoCounts.error}</Tag>
                    )}
                    {sourceData.infoCounts.warning > 0 && (
                      <Tag color="warning" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Warning: {sourceData.infoCounts.warning}</Tag>
                    )}
                    {sourceData.infoCounts.success > 0 && (
                      <Tag color="success" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Success: {sourceData.infoCounts.success}</Tag>
                    )}
                    {sourceData.infoCounts.pending > 0 && (
                      <Tag color="processing" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Pending: {sourceData.infoCounts.pending}</Tag>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {found && !deleted && (
          <div>
            {/* Alerta colapsável de warnings nos dados CapturaWeb — campos vazios */}
            {origin === "capturaweb" && isCnjBasedCapturaWeb && capturaWebDataWarnings.length > 0 && (
              <Alert
                type="warning"
                message={
                  <div
                    onClick={() => setCwWarningsOpen((prev) => !prev)}
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
                  >
                    {cwWarningsOpen ? <DownOutlined style={{ fontSize: 11 }} /> : <RightOutlined style={{ fontSize: 11 }} />}
                    <span>Atenção ({capturaWebDataWarnings.length})</span>
                  </div>
                }
                description={
                  cwWarningsOpen ? (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {capturaWebDataWarnings.map((warn, idx) => (
                        <li key={idx}>{warn}</li>
                      ))}
                    </ul>
                  ) : null
                }
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
            {/* Alerta colapsável de erros nos dados CapturaWeb — exibido acima de "Dados da Fonte" */}
            {origin === "capturaweb" && isCnjBasedCapturaWeb && capturaWebDataErrors.length > 0 && (
              <Alert
                type="error"
                message={
                  <div
                    onClick={() => setCwErrorsOpen((prev) => !prev)}
                    style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
                  >
                    {cwErrorsOpen ? <DownOutlined style={{ fontSize: 11 }} /> : <RightOutlined style={{ fontSize: 11 }} />}
                    <span>Erros detectados ({capturaWebDataErrors.length})</span>
                  </div>
                }
                description={
                  cwErrorsOpen ? (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {capturaWebDataErrors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  ) : null
                }
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <Text strong style={{ fontSize: "14px" }}>
                Dados da Fonte
              </Text>
              <Space>
                {isPush && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      window.open(`/push-details/${sourceId}`, '_blank');
                    }}
                  >
                    Ver push
                  </Button>
                )}
                {isAutorequest && (sourceData?.id || sourceData?.data?.id) && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const autorequestId = sourceData?.id || sourceData?.data?.id;
                      if (autorequestId) {
                        window.open(`/autorequest-details/${autorequestId}`, '_blank');
                      }
                    }}
                  >
                    Ver autorequest
                  </Button>
                )}
              </Space>
            </div>
            <JsonViewer src={sourceData.data} name={origin} collapsed={true} />
          </div>
        )}
        
        {/* Botão aparece quando é push mas não encontrado ou deletado */}
        {isPush && (!found || deleted) && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                window.open(`/push-details/${sourceId}`, '_blank');
              }}
            >
              Ver push
            </Button>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default SourceViewer;
